import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Plus, Pencil, Trash2, ExternalLink, Loader2, X, CheckCircle2, Clock, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import {
  listSeoFixItems,
  upsertSeoFixItem,
  deleteSeoFixItem,
  type SeoFixItem,
  type SeoFixStatus,
  type SeoFixSeverity,
} from "@/lib/seo-fix-items.functions";

export const Route = createFileRoute("/_authenticated/admin/seo-fixes")({
  component: AdminSeoFixesPage,
});

const STATUS_META: Record<SeoFixStatus, { label: string; cls: string; icon: typeof Clock }> = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-900 border-amber-200", icon: Clock },
  in_progress: { label: "In Progress", cls: "bg-blue-100 text-blue-900 border-blue-200", icon: Loader2 },
  fixed: { label: "Fixed", cls: "bg-emerald-100 text-emerald-900 border-emerald-200", icon: CheckCircle2 },
  verified: { label: "Verified", cls: "bg-indigo-100 text-indigo-900 border-indigo-200", icon: ShieldCheck },
};

const SEVERITY_META: Record<SeoFixSeverity, { label: string; cls: string }> = {
  low: { label: "Low", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  medium: { label: "Medium", cls: "bg-yellow-100 text-yellow-900 border-yellow-200" },
  high: { label: "High", cls: "bg-orange-100 text-orange-900 border-orange-200" },
  critical: { label: "Critical", cls: "bg-red-100 text-red-900 border-red-200" },
};

type FormState = {
  id?: string;
  title: string;
  area: string;
  severity: SeoFixSeverity;
  status: SeoFixStatus;
  description: string;
  evidence_url: string;
  notes: string;
  sort_order: number;
};

const EMPTY: FormState = {
  title: "",
  area: "",
  severity: "medium",
  status: "pending",
  description: "",
  evidence_url: "",
  notes: "",
  sort_order: 0,
};

function formatDate(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function AdminSeoFixesPage() {
  const qc = useQueryClient();
  const fetchItems = useServerFn(listSeoFixItems);
  const upsert = useServerFn(upsertSeoFixItem);
  const remove = useServerFn(deleteSeoFixItem);

  const { data: items, isLoading } = useQuery({
    queryKey: ["admin", "seo-fix-items"],
    queryFn: () => fetchItems(),
  });

  const [statusFilter, setStatusFilter] = useState<SeoFixStatus | "all">("all");
  const [editing, setEditing] = useState<FormState | null>(null);

  const upsertMut = useMutation({
    mutationFn: (form: FormState) =>
      upsert({
        data: {
          id: form.id,
          title: form.title.trim(),
          area: form.area.trim() || null,
          severity: form.severity,
          status: form.status,
          description: form.description.trim() || null,
          evidence_url: form.evidence_url.trim() || null,
          notes: form.notes.trim() || null,
          sort_order: Number(form.sort_order) || 0,
        },
      }),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["admin", "seo-fix-items"] });
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message || "Save failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin", "seo-fix-items"] });
    },
    onError: (e: Error) => toast.error(e.message || "Delete failed"),
  });

  const filtered = useMemo(() => {
    if (!items) return [];
    if (statusFilter === "all") return items;
    return items.filter((i) => i.status === statusFilter);
  }, [items, statusFilter]);

  const counts = useMemo(() => {
    const c: Record<SeoFixStatus | "all", number> = {
      all: items?.length ?? 0,
      pending: 0,
      in_progress: 0,
      fixed: 0,
      verified: 0,
    };
    items?.forEach((i) => { c[i.status]++; });
    return c;
  }, [items]);

  const quickStatus = (item: SeoFixItem, status: SeoFixStatus) =>
    upsertMut.mutate({
      id: item.id,
      title: item.title,
      area: item.area ?? "",
      severity: item.severity,
      status,
      description: item.description ?? "",
      evidence_url: item.evidence_url ?? "",
      notes: item.notes ?? "",
      sort_order: item.sort_order,
    });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="size-4" /> Admin
            </Link>
            <div>
              <h1 className="text-xl font-semibold">SEO Fix Tracker</h1>
              <p className="text-xs text-muted-foreground">Track audit items: pending → in progress → fixed → verified</p>
            </div>
          </div>
          <button
            onClick={() => setEditing({ ...EMPTY })}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm hover:opacity-90"
          >
            <Plus className="size-4" /> New Item
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {(["all", "pending", "in_progress", "fixed", "verified"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${
                statusFilter === s
                  ? "bg-foreground text-background border-foreground"
                  : "bg-card hover:bg-muted border-border"
              }`}
            >
              {s === "all" ? "All" : STATUS_META[s].label} <span className="opacity-60 ml-1">({counts[s]})</span>
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="size-5 animate-spin mr-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground border rounded-lg bg-card">
            <AlertCircle className="size-8 mb-2" />
            <p>No items in this view.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((item) => {
              const sMeta = STATUS_META[item.status];
              const StatusIcon = sMeta.icon;
              const sev = SEVERITY_META[item.severity];
              return (
                <article key={item.id} className="rounded-lg border bg-card p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${sMeta.cls}`}>
                          <StatusIcon className={`size-3 ${item.status === "in_progress" ? "animate-spin" : ""}`} />
                          {sMeta.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs border ${sev.cls}`}>{sev.label}</span>
                        {item.area && (
                          <span className="px-2 py-0.5 rounded-full text-xs border bg-muted text-foreground font-mono">{item.area}</span>
                        )}
                      </div>
                      <h2 className="font-medium text-base leading-snug">{item.title}</h2>
                      {item.description && (
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{item.description}</p>
                      )}
                      {item.notes && (
                        <p className="text-xs mt-2 px-2 py-1.5 bg-muted rounded whitespace-pre-wrap">
                          <span className="font-semibold opacity-70">Notes: </span>{item.notes}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                        {item.evidence_url && (
                          <a
                            href={item.evidence_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 underline hover:text-foreground"
                          >
                            <ExternalLink className="size-3" /> Evidence
                          </a>
                        )}
                        <span>Created {formatDate(item.created_at)}</span>
                        <span>Updated {formatDate(item.updated_at)}</span>
                        {item.fixed_at && <span>Fixed {formatDate(item.fixed_at)}</span>}
                        {item.verified_at && <span>Verified {formatDate(item.verified_at)}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {item.status !== "in_progress" && item.status !== "fixed" && item.status !== "verified" && (
                        <button
                          onClick={() => quickStatus(item, "in_progress")}
                          className="text-xs px-2 py-1 rounded border hover:bg-muted"
                        >Start</button>
                      )}
                      {item.status !== "fixed" && item.status !== "verified" && (
                        <button
                          onClick={() => quickStatus(item, "fixed")}
                          className="text-xs px-2 py-1 rounded border hover:bg-muted"
                        >Mark Fixed</button>
                      )}
                      {item.status !== "verified" && (
                        <button
                          onClick={() => quickStatus(item, "verified")}
                          className="text-xs px-2 py-1 rounded border hover:bg-muted"
                        >Verify</button>
                      )}
                      <div className="flex gap-1 mt-1">
                        <button
                          onClick={() => setEditing({
                            id: item.id,
                            title: item.title,
                            area: item.area ?? "",
                            severity: item.severity,
                            status: item.status,
                            description: item.description ?? "",
                            evidence_url: item.evidence_url ?? "",
                            notes: item.notes ?? "",
                            sort_order: item.sort_order,
                          })}
                          className="p-1.5 rounded border hover:bg-muted" aria-label="Edit"
                        ><Pencil className="size-3.5" /></button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete "${item.title}"?`)) deleteMut.mutate(item.id);
                          }}
                          className="p-1.5 rounded border hover:bg-destructive hover:text-destructive-foreground"
                          aria-label="Delete"
                        ><Trash2 className="size-3.5" /></button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <EditDialog
          form={editing}
          onChange={setEditing}
          onSave={() => upsertMut.mutate(editing)}
          onClose={() => setEditing(null)}
          saving={upsertMut.isPending}
        />
      )}
    </div>
  );
}

function EditDialog({
  form, onChange, onSave, onClose, saving,
}: {
  form: FormState;
  onChange: (f: FormState) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-card border rounded-lg shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold">{form.id ? "Edit SEO Fix Item" : "New SEO Fix Item"}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" aria-label="Close">
            <X className="size-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Title *">
            <input
              className="w-full px-3 py-2 border rounded-md bg-background"
              value={form.title}
              onChange={(e) => onChange({ ...form, title: e.target.value })}
              required
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Area / Route">
              <input
                className="w-full px-3 py-2 border rounded-md bg-background font-mono text-sm"
                placeholder="/eat, places, infrastructure…"
                value={form.area}
                onChange={(e) => onChange({ ...form, area: e.target.value })}
              />
            </Field>
            <Field label="Sort Order">
              <input
                type="number"
                className="w-full px-3 py-2 border rounded-md bg-background"
                value={form.sort_order}
                onChange={(e) => onChange({ ...form, sort_order: Number(e.target.value) })}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Severity">
              <select
                className="w-full px-3 py-2 border rounded-md bg-background"
                value={form.severity}
                onChange={(e) => onChange({ ...form, severity: e.target.value as SeoFixSeverity })}
              >
                {(["low", "medium", "high", "critical"] as const).map((s) => (
                  <option key={s} value={s}>{SEVERITY_META[s].label}</option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                className="w-full px-3 py-2 border rounded-md bg-background"
                value={form.status}
                onChange={(e) => onChange({ ...form, status: e.target.value as SeoFixStatus })}
              >
                {(["pending", "in_progress", "fixed", "verified"] as const).map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Description">
            <textarea
              rows={3}
              className="w-full px-3 py-2 border rounded-md bg-background"
              value={form.description}
              onChange={(e) => onChange({ ...form, description: e.target.value })}
            />
          </Field>
          <Field label="Evidence URL">
            <input
              type="url"
              className="w-full px-3 py-2 border rounded-md bg-background"
              placeholder="https://…"
              value={form.evidence_url}
              onChange={(e) => onChange({ ...form, evidence_url: e.target.value })}
            />
          </Field>
          <Field label="Internal Notes">
            <textarea
              rows={2}
              className="w-full px-3 py-2 border rounded-md bg-background"
              value={form.notes}
              onChange={(e) => onChange({ ...form, notes: e.target.value })}
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-muted">Cancel</button>
          <button
            onClick={onSave}
            disabled={saving || !form.title.trim()}
            className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}
